"use client";

import React from "react";
import { motion } from "framer-motion";
import { TriangleAlert as AlertTriangle, ArrowRight, Droplets, Flame, PhoneCall, ShieldAlert, Sparkles, Waves, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const problemCategories = [{
  id: "burst-pipes",
  title: "Burst Pipes & Major Flooding",
  badge: "Immediate Hazard",
  urgency: "High Emergency",
  description: "Ruptured supply lines, freezing fractures, or wall leaks causing active water pooling in your property.",
  commonSigns: "Hissing wall sounds, sudden pressure drop, pooling water",
  avgResponse: "30-45 min",
  iconName: "Droplets",
  actionUrl: "/services/"
}, {
  id: "sewer-backups",
  title: "Sewer & Main Line Clogs",
  badge: "Health Risk",
  urgency: "High Emergency",
  description: "Multiple slow drains, sewage odors, or wastewater backing up through floor drains and shower pans.",
  commonSigns: "Gurgling toilets, foul sewer smells, multiple backed-up fixtures",
  avgResponse: "30-45 min",
  iconName: "ShieldAlert",
  actionUrl: "/services/"
}, {
  id: "water-heater",
  title: "Water Heater Breakdown & Leaks",
  badge: "Fast Dispatch",
  urgency: "Priority",
  description: "Total loss of hot water, tank leaking from base, metallic rusty water, or strange banging noises.",
  commonSigns: "Lukewarm water, puddles near tank, pilot light failure",
  avgResponse: "Same Day / 24/7",
  actionUrl: "/water-heater/"
}, {
  id: "toilet-overflow",
  title: "Toilet Overflow & Fixture Failure",
  badge: "Quick Resolution",
  urgency: "Priority",
  description: "Toilets actively overflowing, stuck fill valves, leaking shutoff valves, or cracked porcelain bases.",
  commonSigns: "Uncontrolled bowl filling, faulty shutoff valve, floor leak",
  avgResponse: "30-45 min",
  iconName: "Waves",
  actionUrl: "/services/"
}, {
  id: "gas-pipe",
  title: "Gas Line & Smell Emergencies",
  badge: "Critical Danger",
  urgency: "Urgent Action",
  description: "Rotten egg odor, whistling near gas appliances, or damaged flexible supply lines requiring shutoff.",
  commonSigns: "Sulfur scent, pilot outage, visible pipe corrosion",
  avgResponse: "Immediate",
  iconName: "Flame",
  actionUrl: "/contact/"
}, {
  id: "low-pressure",
  title: "Hidden Leaks & Pressure Drops",
  badge: "Diagnostic",
  urgency: "Standard Emergency",
  description: "Sudden unexplainable loss in water flow throughout the house or unmetered utility spikes.",
  commonSigns: "Trickling faucets, spinning water meter, damp drywall",
  avgResponse: "Same Day",
  iconName: "Wrench",
  actionUrl: "/services/"
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function ProblemCategories() {
  const getCategoryIcon = (iconName?: string) => {
    switch (iconName) {
      case "Droplets":
        return <Droplets className="size-5 text-primary" />;
      case "ShieldAlert":
        return <ShieldAlert className="size-5 text-destructive" />;
      case "Flame":
        return <Flame className="size-5 text-destructive" />;
      case "Waves":
        return <Waves className="size-5 text-primary" />;
      case "Wrench":
        return <Wrench className="size-5 text-primary" />;
      default:
        return <AlertTriangle className="size-5 text-primary" />;
    }
  };
  return <section id="relatedproblemcategories" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-medium bg-secondary text-secondary-foreground">
            <Sparkles className="size-3.5 mr-1" />
            Troubleshooting & Diagnostic Guide
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Related Plumbing Emergencies
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            Identify the issue you are facing in your Jonesboro home or business.
            Select a problem category below for immediate steps, signs, and 24/7 service solutions.
          </p>
        </div>

        {/* Problem Categories Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.1
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {problemCategories.map((item, i) => <motion.div key={item.id} variants={itemVariants} data-index={i} className="h-full flex">
              <Card className="flex flex-col justify-between w-full rounded-xl border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200 hover:border-primary/40">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground">
                      {getCategoryIcon(item.iconName)}
                    </div>
                    <Badge variant={item.urgency.includes("High") || item.urgency.includes("Critical") ? "destructive" : "outline"} className="text-xs">
                      {item.badge}
                    </Badge>
                  </div>
                  <CardTitle className="text-xl font-bold text-card-foreground">
                    {item.title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-2 leading-relaxed">
                    {item.description}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-3 py-0">
                  <div className="p-3 rounded-lg bg-muted/60 border border-border/50 text-xs">
                    <span className="font-semibold text-foreground block mb-1">
                      Common Symptoms:
                    </span>
                    <span className="text-muted-foreground">{item.commonSigns}</span>
                  </div>

                  <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
                    <span>Target Response:</span>
                    <span className="font-semibold text-foreground">{item.avgResponse}</span>
                  </div>
                </CardContent>

                <CardFooter className="pt-5 mt-auto">
                  <div className="w-full">
                    <Separator className="mb-4" />
                    <Button asChild variant="outline" className="w-full justify-between hover:bg-primary hover:text-primary-foreground group">
                      <Link to={item.actionUrl}>
                        <span>Explore Solutions</span>
                        <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Emergency Callout Banner */}
        <div className="mt-12 md:mt-16 rounded-2xl bg-secondary/80 border border-border p-6 sm:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-1 text-center sm:text-left">
            <h3 className="text-lg sm:text-xl font-bold text-foreground">
              Do not see your exact plumbing problem?
            </h3>
            <p className="text-sm text-muted-foreground max-w-xl">
              Our Jonesboro emergency dispatchers can assess your situation over the phone and
              send a licensed plumber straight away.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="default" size="lg" className="shadow-sm">
              <Link to="/contact/">
                <PhoneCall className="size-4" />
                <span>Call Dispatch 24/7</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg">
              <Link to="/services/">
                <span>View All Services</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}